import { useMemo } from "react"
import { DashboardAnalyticsContext } from "./types"
import { InstrumentWithMetadata } from "helpers/types/instruments/metadata"
import { SelectedInstrument } from "helpers/types/instruments/selected"

// TODO: Make all of these types better
type SelectedInstrumentWithOptionalMetadata = InstrumentWithMetadata | SelectedInstrument | undefined | null

interface InstrumentsMetadata {
  original?: SelectedInstrumentWithOptionalMetadata
  comparison?: SelectedInstrumentWithOptionalMetadata
  base?: SelectedInstrumentWithOptionalMetadata
}

export interface DashboardAnalyticsFields {
  modelId: string | null
  selectedMode: unknown
  period: unknown
  isIntraday: boolean
  isEndVsStartDateComparison: boolean
  instrumentsMetadata: InstrumentsMetadata | null | undefined
  instrument: unknown
  comparisonInstrument: unknown
  base: unknown
  secondInstrument: unknown
}

type InstrumentType = "ETF" | "BASKET" | "INDEX" | "BENCHMARK" | "PORTFOLIO"

const getInstrumentId = (instrument: SelectedInstrumentWithOptionalMetadata): string | undefined => {
  if (!instrument) return undefined
  return "alias" in instrument ? instrument.alias : instrument.id
}

const getInstrumentName = (instrument: SelectedInstrumentWithOptionalMetadata): string | undefined => {
  if (!instrument) return undefined
  if ("name" in instrument && typeof instrument.name === "string") return instrument.name
  if ("description" in instrument && typeof instrument.description === "string") return instrument.description
  return undefined
}

const getInstrumentType = (instrument: SelectedInstrumentWithOptionalMetadata): InstrumentType | undefined => {
  return instrument?.type as InstrumentType | undefined
}

/**
 * Only recomputes when primitive analytics values change.
 *
 * @param fields - Dashboard state fields for analytics tracking
 * @returns Memoized dashboard analytics properties (all optional based on dashboard state)
 */
export function useExtractDashboardContextProperties(
  fields: DashboardAnalyticsFields,
): Partial<DashboardAnalyticsContext> {
  const {
    modelId,
    selectedMode,
    period,
    isIntraday,
    isEndVsStartDateComparison,
    instrumentsMetadata,
    instrument,
    comparisonInstrument,
    base,
    secondInstrument,
  } = fields

  const original = instrumentsMetadata?.original ?? (instrument as SelectedInstrumentWithOptionalMetadata)
  const instrumentId = getInstrumentId(original)
  const instrumentName = getInstrumentName(original)
  const instrumentType = getInstrumentType(original)

  const comparison = instrumentsMetadata?.comparison ?? (comparisonInstrument as SelectedInstrumentWithOptionalMetadata)
  const comparisonInstrumentId = getInstrumentId(comparison)
  const comparisonInstrumentName = getInstrumentName(comparison)
  const comparisonInstrumentType = getInstrumentType(comparison)

  const baseInstrument = instrumentsMetadata?.base ?? (base as SelectedInstrumentWithOptionalMetadata)
  const baseId = getInstrumentId(baseInstrument)
  const baseName = getInstrumentName(baseInstrument)
  const baseType = getInstrumentType(baseInstrument)

  const hasComparison = Boolean(comparison)
  const hasSecondInstrument = Boolean(secondInstrument)
  const hasBase = Boolean(baseInstrument)

  return useMemo(
    () => ({
      modelId: modelId ?? undefined,
      instrumentId,
      instrumentName,
      instrumentType,
      comparisonInstrumentId,
      comparisonInstrumentName,
      comparisonInstrumentType,
      baseId,
      baseName,
      baseType,
      selectedMode: selectedMode as string | undefined,
      hasComparison,
      hasSecondInstrument,
      hasBase,
      period: period as string | undefined,
      isIntraday,
      isEndVsStartDateComparison,
    }),
    [
      modelId,
      instrumentId,
      instrumentName,
      instrumentType,
      comparisonInstrumentId,
      comparisonInstrumentName,
      comparisonInstrumentType,
      baseId,
      baseName,
      baseType,
      selectedMode,
      hasComparison,
      hasSecondInstrument,
      hasBase,
      period,
      isIntraday,
      isEndVsStartDateComparison,
    ],
  )
}
